import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaShoppingCart, 
  FaBars, 
  FaTimes, 
  FaUser,
  FaHome,
  FaStore,
  FaBlog,
  FaInfoCircle,
  FaEnvelope,
  FaSignInAlt,
  FaUserShield,
  FaChartLine,
  FaTrophy
} from 'react-icons/fa';
import { auth, db } from '../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import styles from './Navbar.module.css';
import logo from '../../../assets/images/logo.png';

const navTranslations = {
  en: {
    home: 'Home',
    shop: 'Shop',
    blog: 'Blog',
    winners: 'Winners',
    about: 'About',
    contact: 'Contact',
    admin: 'Admin Dashboard',
    marketer: 'Marketer Dashboard',
    account: 'Account',
    myAccount: 'My Account',
    login: 'Login',
    logout: 'Logout',
  },
  ar: {
    home: 'الرئيسية',
    shop: 'المتجر',
    blog: 'المدونة',
    winners: 'الفائزون',
    about: 'من نحن',
    contact: 'اتصل بنا',
    admin: 'لوحة تحكم المدير',
    marketer: 'لوحة تحكم المسوق',
    account: 'الحساب',
    myAccount: 'حسابي',
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
  },
  tr: {
    home: 'Ana Sayfa',
    shop: 'Mağaza',
    blog: 'Blog',
    winners: 'Kazananlar',
    about: 'Hakkımızda',
    contact: 'İletişim',
    admin: 'Yönetici Paneli',
    marketer: 'Pazarlamacı Paneli',
    account: 'Hesap',
    myAccount: 'Hesabım',
    login: 'Giriş Yap',
    logout: 'Çıkış Yap',
  }
};

const Navbar = ({ isDarkMode, toggleTheme, cartItems, lang, setLang }) => {
  const [isOpen, setIsOpen] = useState(false); // For mobile menu
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // New state for dropdown
  const navigate = useNavigate();

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const t = navTranslations[lang];

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      } else {
        setUserRole(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setIsDropdownOpen(false); // Close dropdown on logout
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen); // Toggle dropdown state
  };

  return (
    <nav className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.navContainer}>
        <div className={styles.logoRow}>
          <Link to="/" className={styles.logo}>
            <img src={logo} alt="2Nice Logo" className={styles.logoImage} />
          </Link>
          <div className={`${styles.navLinks} ${isOpen ? styles.active : ''}`}>
            <Link to="/" className={styles.navLink} onClick={() => setIsOpen(false)}>
              <FaHome className={styles.navIcon} />
              <span>{t.home}</span>
            </Link>
            <Link to="/shop" className={styles.navLink} onClick={() => setIsOpen(false)}>
              <FaStore className={styles.navIcon} />
              <span>{t.shop}</span>
            </Link>
            <Link to="/blog" className={styles.navLink} onClick={() => setIsOpen(false)}>
              <FaBlog className={styles.navIcon} />
              <span>{t.blog}</span>
            </Link>
            <Link to="/winners" className={styles.navLink} onClick={() => setIsOpen(false)}>
              <FaTrophy className={styles.navIcon} />
              <span>{t.winners}</span>
            </Link>
            <Link to="/about" className={styles.navLink} onClick={() => setIsOpen(false)}>
              <FaInfoCircle className={styles.navIcon} />
              <span>{t.about}</span>
            </Link>
            <Link to="/contact" className={styles.navLink} onClick={() => setIsOpen(false)}>
              <FaEnvelope className={styles.navIcon} />
              <span>{t.contact}</span>
            </Link>
            {/* Modern compact language switcher */}
            <select className={styles.langSwitcherCompact} value={lang} onChange={e => setLang(e.target.value)} aria-label="Language Switcher">
              <option value="en">EN</option>
              <option value="ar">AR</option>
              <option value="tr">TR</option>
            </select>
            {userRole === 'admin' && (
              <Link to="/admin" className={styles.navLink} onClick={() => setIsOpen(false)}>
                <FaUserShield className={styles.navIcon} />
                <span>{t.admin}</span>
              </Link>
            )}
            {userRole === 'marketer' && (
              <Link to="/marketer" className={styles.navLink} onClick={() => setIsOpen(false)}>
                <FaChartLine className={styles.navIcon} />
                <span>{t.marketer}</span>
              </Link>
            )}
          </div>
          <div className={styles.navActions}>
            <Link to="/cart" className={styles.cartLink}>
              <FaShoppingCart className={styles.cartIcon} />
              {cartCount > 0 && <span className={styles.cartCount}>{cartCount}</span>}
            </Link>
            <button className={styles.themeToggle} onClick={toggleTheme}>
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            <div className={styles.userMenu}>
              <button 
                className={styles.userButton} 
                onClick={toggleDropdown}
              >
                <FaUser />
                {/* Removed account text, icon only */}
              </button>
              <div className={`${styles.dropdownMenu} ${isDropdownOpen ? styles.active : ''}`}>
                {user ? (
                  <>
                    <button 
                      onClick={handleLogout} 
                      className={styles.dropdownItem}
                    >
                      <FaSignInAlt className={styles.dropdownIcon} />
                      {t.logout}
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/login" 
                    className={styles.dropdownItem} 
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <FaSignInAlt className={styles.dropdownIcon} />
                    {t.login}
                  </Link>
                )}
              </div>
            </div>
            <button className={styles.menuButton} onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;